import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { dataHora, rotulo } from '../formato';
import { useSessao } from '../sessao';
import {
  AcoesDoLaudo, Area, Aviso, Carregando, ErroDaApi, Rn, SegurarParaConfirmar,
  Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';

/**
 * A passagem de mão do ensaio.
 *
 * «Atualmente a parte de aprovação não estou encontrando na interface
 *  final que está na web» — e não estava mesmo. O fluxo do §12 existia no
 * enum desde a `0001` e não tinha rota nem tela: um ensaio nascia
 * `programado` e morria `programado`, com o laudo saindo por baixo.
 *
 * ── Por que uma tela e não botões espalhados ─────────────────────────
 *
 * A tentação era pendurar «aprovar» na linha da fila. O problema é que
 * aprovar não é uma ação isolada: é a última de uma sequência em que
 * importa quem fez o quê antes. Um botão solto pede uma decisão sem
 * mostrar o que a fundamenta — e quem aprova precisa ver, na mesma tela,
 * que outra pessoa verificou, quando, e se o ensaio já voltou antes.
 *
 * Então a tela mostra a escada inteira e o histórico embaixo, e os botões
 * são o que sobra depois disso.
 *
 * ── Os botões que aparecem ───────────────────────────────────────────
 *
 * Só os que a etapa atual permite E que o perfil pode executar. Mostrar
 * um botão que vai recusar é pior que escondê-lo: ensina o operador a
 * apertar coisas e ver o que acontece. Mas quando a etapa permite e o
 * perfil não, a tela DIZ de quem é a vez — senão o ensaio some numa fila
 * invisível e o técnico fica esperando sem saber quem.
 */

interface Situacao {
  ensaio_id: string;
  os_id: string;
  amostra_id: string;
  status: string;
  etapa: string;
  executado_por: string | null;
  executado_por_nome: string | null;
  data_execucao: string | null;
  processado_por: string | null;
  processado_por_nome: string | null;
  data_processamento: string | null;
  verificado_por: string | null;
  verificado_por_nome: string | null;
  data_verificacao: string | null;
  aprovado_por: string | null;
  aprovado_por_nome: string | null;
  data_aprovacao: string | null;
  motivo_devolucao: string | null;
  versao_calculo_congelada: boolean;
  devolucoes: string | number;
  /** A programação da OS está aberta — cancelar vai funcionar (0156). */
  programacao_aberta?: boolean | null;
  /** RN-265 (0268): a mesma pessoa assinou mais de um passo, pelos superpoderes da homologação. */
  assinatura_propria?: boolean;
}

/** O que o servidor responde sobre a subida automática (`0118`). */
interface Avanco {
  ensaio_id: string;
  status: string;
  no_meio_da_escada: boolean;
  tem_dado: boolean;
  ficha: string | null;
  anda_sozinho: boolean;
  trava: string[] | null;
  pode_subir: boolean;
}

interface Passagem {
  evento: 'status' | 'verificacao';
  de: string | null;
  para: string;
  datahora: string;
  motivo: string | null;
  quem: string | null;
}

const TOM: Record<string, Tom> = {
  programado: 'neutro',
  em_execucao: 'andamento',
  pausado: 'atencao',
  dados_completos: 'andamento',
  em_processamento: 'andamento',
  aguardando_verificacao: 'atencao',
  aguardando_aprovacao: 'atencao',
  devolvido_para_correcao: 'critico',
  aprovado: 'ok',
  cancelado: 'neutro',
  substituido: 'neutro',
};

/**
 * O que a tela oferece em cada etapa.
 *
 * Espelha o grafo da `0061` — e espelhar é a palavra certa: a lista que
 * VALE é a de lá. Esta existe para não desenhar um botão que o banco vai
 * recusar. Se as duas divergirem, quem manda é o banco, e o sintoma será
 * um botão que dá 409 — irritante, mas honesto.
 */
/**
 * Toda decisão humana passa por uma confirmação segurada.
 *
 * «Para essas opções deve abrir um pop-up de confirmação ou botão que
 *  deve ser segurado durante X segundos (no caso de dispositivos
 *  móveis)» — Humberto, 01/09/2026.
 *
 * `explica` não é enfeite: é o que a pessoa lê DURANTE o segundo e meio
 * em que a mão está presa. Uma confirmação sem texto novo — «tem
 * certeza?» — não acrescenta informação nenhuma à decisão; ela só
 * acrescenta um clique, e clique a mais a mão aprende a dar sozinha.
 */
interface Acao {
  para: string;
  texto: string;
  permissao: string;
  explica: string;
  pedeMotivo?: boolean;
  tom?: 'perigo';
}

const CANCELAR: Acao = {
  para: 'cancelado', texto: 'Cancelar ensaio', permissao: 'os.cancelar',
  pedeMotivo: true, tom: 'perigo',
  explica: 'O ensaio para aqui e não volta. Cancelado é estado terminal — '
    + 'ressuscitar um ensaio cancelado seria a porta pela qual um '
    + 'resultado ruim vira um bom. Se o serviço for refeito, é ensaio novo.',
};

const ACOES: Record<string, Acao[]> = {
  programado: [
    { para: 'em_execucao', texto: 'Iniciar execução', permissao: 'ensaio.lancar',
      explica: 'Marca o começo do trabalho de bancada com o seu nome e a '
        + 'hora. Normalmente não é preciso: o primeiro dado lançado já '
        + 'move o ensaio sozinho.' },
    CANCELAR,
  ],
  /*
   * «Dados completos» e «Processar» saíram (`0118`).
   *
   * Os dois pediam que a pessoa AFIRMASSE o que o servidor já sabe — e
   * um botão que só pode ser apertado quando a resposta já é sim vira
   * assinatura em branco: aperta-se sem reler. Agora a ficha sobe
   * sozinha no instante em que a última pendência que trava fecha, e o
   * que sobra na tela é só o que é decisão de gente.
   */
  em_execucao: [
    { para: 'pausado', texto: 'Pausar', permissao: 'ensaio.lancar',
      explica: 'O ensaio sai da fila de trabalho e para de andar sozinho — '
        + 'nem que a ficha feche. Só alguém retomando o traz de volta.' },
    CANCELAR,
  ],
  pausado: [
    { para: 'em_execucao', texto: 'Retomar', permissao: 'ensaio.lancar',
      explica: 'O ensaio volta para a bancada e para a fila, e volta a '
        + 'andar sozinho quando a ficha fechar.' },
    CANCELAR,
  ],
  dados_completos: [
    { para: 'em_execucao', texto: 'Reabrir lançamento', permissao: 'ensaio.reabrir',
      explica: 'Devolve o ensaio para a bancada. Relançar dado bruto sobre '
        + 'um ensaio já processado muda o resultado, e a reabertura fica '
        + 'no histórico com o seu nome.' },
    CANCELAR,
  ],
  em_processamento: [
    { para: 'em_execucao', texto: 'Reabrir lançamento', permissao: 'ensaio.reabrir',
      explica: 'Devolve o ensaio para a bancada. Relançar dado bruto sobre '
        + 'um ensaio já processado muda o resultado, e a reabertura fica '
        + 'no histórico com o seu nome.' },
    CANCELAR,
  ],
  aguardando_verificacao: [
    { para: 'devolvido_para_correcao', texto: 'Devolver para correção',
      permissao: 'ensaio.verificar', pedeMotivo: true, tom: 'perigo',
      explica: 'O ensaio volta para quem executou, com o que você escrever '
        + 'aqui. A verificação anterior fica no histórico e a coluna de '
        + 'verificador esvazia: o ensaio corrigido precisa ser conferido '
        + 'de novo.' },
    CANCELAR,
  ],
  aguardando_aprovacao: [
    { para: 'aprovado', texto: 'Aprovar', permissao: 'ensaio.aprovar',
      explica: 'Aprovado é pedra: os dados deste ensaio não se alteram '
        + 'mais, e a versão de cálculo fica congelada. Corrigir um '
        + 'resultado aprovado passa a ser um ensaio NOVO apontando para '
        + 'este (§25). É a assinatura que sai no laudo.' },
    { para: 'devolvido_para_correcao', texto: 'Devolver para correção',
      permissao: 'ensaio.verificar', pedeMotivo: true, tom: 'perigo',
      explica: 'O ensaio volta para quem executou, com o que você escrever '
        + 'aqui. A verificação anterior fica no histórico e a coluna de '
        + 'verificador esvazia.' },
    CANCELAR,
  ],
  /*
   * Devolvido não sobe sozinho, e por isso os dois botões continuam.
   * A devolução tem motivo escrito, e o motivo quase nunca é «falta
   * dado» — é «este valor não bate». Quem corrigiu diz que corrigiu.
   */
  devolvido_para_correcao: [
    { para: 'em_processamento', texto: 'Reprocessar', permissao: 'ensaio.processar',
      pedeMotivo: true,
      explica: 'Diz que a correção pedida foi feita e devolve o ensaio para '
        + 'a verificação — outra pessoa vai conferir de novo. Escreva o que '
        + 'foi corrigido: é a resposta ao motivo da devolução, e é o que o '
        + 'verificador lê primeiro (UX-047).' },
    { para: 'em_execucao', texto: 'Voltar ao lançamento', permissao: 'ensaio.lancar',
      explica: 'Devolve o ensaio para a bancada, para relançar dado.' },
    CANCELAR,
  ],
};

/** De quem é a vez, quando não é a minha. */
const DE_QUEM: Record<string, string> = {
  programado: 'do técnico, para iniciar',
  em_execucao: 'do técnico, na bancada',
  pausado: 'do técnico, para retomar',
  dados_completos: 'de quem processa os resultados',
  em_processamento: 'de quem processa os resultados',
  aguardando_verificacao: 'de um verificador — alguém que não executou',
  aguardando_aprovacao: 'do responsável técnico',
  devolvido_para_correcao: 'de quem executou, para corrigir',
};

export function EnsaioSituacao() {
  const { id = '' } = useParams();
  const { pode, eu } = useSessao();
  const [s, setS] = useState<Situacao | null>(null);
  const [av, setAv] = useState<Avanco | null>(null);
  const [em, setEm] = useState<Emissao | null>(null);
  const [emitindo, setEmitindo] = useState(false);
  const [hist, setHist] = useState<Passagem[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  /* O que deu certo — em verde, e não na caixa de «Não deu» (UX-050). */
  const [feito, setFeito] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [pedindo, setPedindo] = useState<Acao | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      const [a, b, c, e] = await Promise.all([
        api<Situacao>(`/ensaios/${id}/situacao`),
        api<Passagem[]>(`/ensaios/${id}/historico`),
        api<Avanco>(`/ensaios/${id}/avanco`),
        api<Emissao>(`/ensaios/${id}/emissao`).catch(() => null),
      ]);
      setS(a);
      setHist(b);
      setAv(c);
      setEm(e);
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  const mover = async (para: string, comMotivo?: string) => {
    setOcupado(true);
    setAviso(null);
    try {
      await api(`/ensaios/${id}/situacao`, {
        metodo: 'POST',
        corpo: comMotivo ? { para, motivo: comMotivo } : { para },
      });
      setPedindo(null);
      setMotivo('');
      await carregar();
    } catch (e) {
      setAviso(mensagemDoErro(e));
    } finally { setOcupado(false); }
  };

  /* O empurrão do legado — a mesma função do gatilho, sem decisão nova. */
  const empurrar = async () => {
    setOcupado(true);
    setAviso(null);
    try {
      await api(`/ensaios/${id}/avancar`, { metodo: 'POST', corpo: {} });
      await carregar();
    } catch (e) {
      setAviso(mensagemDoErro(e));
    } finally { setOcupado(false); }
  };

  const verificar = async () => {
    setOcupado(true);
    setAviso(null);
    try {
      await api(`/ensaios/${id}/verificar`, { metodo: 'POST', corpo: {} });
      await carregar();
    } catch (e) {
      setAviso(mensagemDoErro(e));
    } finally { setOcupado(false); }
  };

  if (erro) return <ErroDaApi erro={erro} />;
  if (!s) return <Carregando texto="Carregando a situação do ensaio" />;

  const acoes = (ACOES[s.etapa] ?? []).filter((a) => pode(a.permissao));
  const bloqueadas = (ACOES[s.etapa] ?? []).filter((a) => !pode(a.permissao));
  const devolucoes = Number(s.devolucoes ?? 0);

  /*
   * Verificar é a única ação que não muda status, então não cabe em
   * ACOES — ela vive à parte.
   *
   * E ela tem uma condição que nenhuma outra tem: **quem executou não
   * verifica**, mesmo tendo a permissão. O §15 é sobre a PESSOA, não
   * sobre o perfil — o gestor de laboratório tem `ensaio.verificar` e
   * ainda assim não confere o ensaio que ele mesmo rodou.
   *
   * Filtrar por permissão sozinho deixaria o botão aparecer para ele. O
   * banco recusaria, e a tela teria ensinado a apertar e ver no que dá.
   */
  /* RN-265 (0268): na homologação, a conta com os superpoderes verifica o
     que ela mesma executou — o banco deixa e marca o ensaio. */
  const souOExecutor = !!s.executado_por && s.executado_por === eu?.usuarioId
    && !eu?.superpoderes;
  const podeVerificar = s.etapa === 'aguardando_verificacao'
    && pode('ensaio.verificar') && !souOExecutor;

  return (
    <div>
      <div className="cabeca">
        <h1>Situação do ensaio</h1>
        <p className="sub">
          Por quantas mãos ele passou, e de quem é a vez agora
        </p>
      </div>

      <div className="cartao">
        <div className="linha-selo">
          <Selo tom={TOM[s.etapa] ?? 'neutro'}>{rotulo(s.etapa)}</Selo>
          {devolucoes > 0 && (
            <span className="sub">
              devolvido {devolucoes} {devolucoes === 1 ? 'vez' : 'vezes'}
            </span>
          )}
        </div>

        {s.motivo_devolucao && s.status === 'devolvido_para_correcao' && (
          <Aviso tipo="erro" titulo="Pendência a corrigir">
            {s.motivo_devolucao}
          </Aviso>
        )}

        {/*
          * A escada, com nome e data de cada mão. Uma etapa sem dono é
          * uma etapa que ainda não aconteceu — e é assim que ela aparece,
          * em vez de sumir da lista. O vazio é informação: mostra o que
          * falta sem obrigar ninguém a saber a ordem de cor.
          */}
        <ol className="escada-do-ensaio">
          <Degrau papel="Executou"   nome={s.executado_por_nome}
                  quando={s.data_execucao} />
          <Degrau papel="Processou"  nome={s.processado_por_nome}
                  quando={s.data_processamento} />
          <Degrau papel="Verificou"  nome={s.verificado_por_nome}
                  quando={s.data_verificacao} />
          <Degrau papel="Aprovou"    nome={s.aprovado_por_nome}
                  quando={s.data_aprovacao} />
        </ol>

        {s.assinatura_propria && (
          <p className="nota" data-teste="assinatura-propria">
            A mesma pessoa assinou mais de um passo deste ensaio — liberação
            da homologação <Rn codigo="RN-265"
              diz="Na homologação, a conta com os superpoderes lança, verifica e aprova o próprio trabalho; o registro fica marcado." />.
          </p>
        )}

        {s.status === 'aprovado' && (
          <p className="nota">
            Ensaio aprovado. Os dados dele não se alteram mais — corrigir
            um resultado aprovado é gerar um ensaio novo apontando para
            este.
          </p>
        )}

        <Emitir
          id={id}
          em={em}
          pode={pode('relatorio.emitir')}
          ocupado={emitindo}
          aoEmitir={async () => {
            setEmitindo(true);
            setAviso(null); setFeito(null);
            try {
              const r = await api<{ numero: string; revisao: number }>(
                `/ensaios/${id}/emitir`, { metodo: 'POST' });
              setFeito(`Laudo ${r.numero} · R${
                String(r.revisao).padStart(2, '0')} emitido.`);
              await carregar();
            } catch (e) { setErro(e); } finally { setEmitindo(false); }
          }}
        />

        {/*
          * O que falta, o tempo todo — e não só depois de um clique que
          * deu errado (`0118`).
          *
          * Antes desta onda a lista de pendências só aparecia como
          * mensagem de ERRO, quando alguém tentava empurrar o ensaio e o
          * banco recusava. Quer dizer: a informação existia e era
          * entregue como repreensão. Agora ela está na tela desde o
          * começo, que é quando serve para alguma coisa — o técnico vê o
          * que ainda tem de fazer enquanto ainda está na bancada.
          */}
        {av && av.no_meio_da_escada && av.anda_sozinho && (
          (av.trava ?? []).length > 0 ? (
            <div className="falta-para-subir">
              <h3>Falta para este ensaio seguir</h3>
              <ul>
                {(av.trava ?? []).map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              <p className="sub">
                Assim que a última fechar, o ensaio segue sozinho para a
                verificação — não há botão a apertar.
              </p>
            </div>
          ) : (
            <p className="nota">
              Nada trava esta ficha. Ela sobe sozinha para a verificação na
              próxima gravação — ou agora, se você mandar.
            </p>
          )
        )}

        {av && av.no_meio_da_escada && !av.anda_sozinho && (
          <p className="nota">
            Esta ficha ainda não anda sozinha: o sistema não sabe dizer
            quando ela fecha. Enquanto isso, o ensaio segue pelos botões.
          </p>
        )}

        {aviso && <Aviso tipo="erro" titulo="Não deu">{aviso}</Aviso>}
        {feito && <Aviso tipo="ok" titulo="Feito">{feito}</Aviso>}

        <div className="acoes">
          {podeVerificar && (
            <button type="button" className="botao" disabled={ocupado}
                    onClick={() => void verificar()}>
              Verificar
            </button>
          )}

          {/*
            * A porta de saída do legado.
            *
            * Só aparece para o ensaio que fechou ANTES da 0118 e ficou
            * parado esperando um botão que não existe mais. Ela some
            * sozinha à medida que o legado passa — e não aparece nunca
            * para uma ficha aberta, porque ali `pode_subir` é falso.
            */}
          {av?.pode_subir && pode('ensaio.lancar') && (
            <button type="button" className="botao" disabled={ocupado}
                    onClick={() => void empurrar()}>
              Seguir para verificação
            </button>
          )}

          {/*
            * Nenhum destes grava direto (`0118`).
            *
            * Todos são decisão humana e nenhum é barato de desfazer —
            * pausar tira o ensaio da fila, reabrir muda resultado,
            * devolver zera a verificação, cancelar não volta, aprovar é
            * pedra. O clique aqui só ABRE a confirmação; quem grava é o
            * botão que se segura, e o texto que a pessoa lê enquanto
            * segura é diferente para cada um.
            */}
          {acoes.map((a) => {
            /*
             * Cancelar só é oferecido quando vai funcionar (UX-058).
             *
             * Desde a 0161 a programação de OS viva está sempre aberta; o
             * que ainda fecha a porta é a OS cancelada. O botão fica
             * desligado, com o porquê ao lado.
             */
            const travado = a.para === 'cancelado' && s.programacao_aberta === false;
            return (
              <button key={a.para + a.texto} type="button"
                      className={a.tom === 'perigo' ? 'botao perigo' : 'botao'}
                      disabled={ocupado || travado}
                      title={travado
                        ? 'A OS está cancelada: a programação não muda mais.'
                        : undefined}
                      onClick={() => { setMotivo(''); setPedindo(a); }}>
                {a.texto}
              </button>
            );
          })}
          {acoes.some((a) => a.para === 'cancelado') && s.programacao_aberta === false && (
            <p className="nota">
              A <Link to={`/os/${s.os_id}`}>OS</Link> está cancelada: a
              programação dela não muda mais.
            </p>
          )}

          {acoes.length === 0 && !podeVerificar && DE_QUEM[s.etapa] && (
            <p className="nota">
              A vez agora é {DE_QUEM[s.etapa]}.
              {bloqueadas.length > 0
                && ' Seu perfil não executa esta etapa.'}
            </p>
          )}
        </div>

        {/*
          * Esconder o botão sem explicar é metade do trabalho.
          *
          * Quem executou e TEM a permissão de verificar não encontra o
          * botão, e sem esta linha vai procurar o defeito na tela — ou
          * pior, pedir a senha de outra pessoa. A regra é do §15 e não é
          * segredo: dizê-la é o que faz o operador chamar o verificador
          * em vez de chamar o suporte.
          */}
        {souOExecutor && s.etapa === 'aguardando_verificacao'
          && pode('ensaio.verificar') && (
          <p className="nota">
            Você executou este ensaio. A verificação é de outra pessoa —
            quem faz não confere o próprio trabalho (§15).
          </p>
        )}
        {eu?.superpoderes && s.executado_por === eu.usuarioId
          && (s.etapa === 'aguardando_verificacao' || s.etapa === 'aguardando_aprovacao') && (
          <p className="nota" data-teste="superpoderes">
            Homologação: você executou este ensaio e pode verificá-lo e
            aprová-lo — o ensaio fica marcado como assinado pela mesma
            pessoa <Rn codigo="RN-265" />.
          </p>
        )}

        {/*
          * Devolver pede o motivo antes de mandar, e não depois de o
          * banco recusar. A regra é a mesma dos dois lados — o §12 pede a
          * pendência registrada — mas descobrir isso por erro faz o
          * verificador escrever qualquer coisa só para passar.
          */}
        {pedindo && (
          <div className="cartao aninhado confirmacao">
            <h3>{pedindo.texto}</h3>
            <p className="sub">{pedindo.explica}</p>

            {pedindo.pedeMotivo && (
              <Area
                rotulo={pedindo.para === 'cancelado'
                  ? 'Por que este ensaio está sendo cancelado'
                  : pedindo.para === 'em_processamento'
                    ? 'O que foi corrigido'
                    : 'O que precisa ser corrigido'}
                value={motivo}
                rows={3}
                onChange={(e) => setMotivo(e.currentTarget.value)}
                dica="Vai para o histórico, e é o que a próxima pessoa lê." />
            )}

            <p className="sub" id="segurar-dica">
              Segure o botão até a barra encher. Soltar antes cancela.
            </p>
            <div className="acoes">
              <SegurarParaConfirmar
                texto={pedindo.texto}
                tom={pedindo.tom === 'perigo' ? 'perigo' : 'normal'}
                desabilitado={ocupado
                  || (!!pedindo.pedeMotivo && motivo.trim().length < 5)}
                aoConfirmar={() => void mover(
                  pedindo.para,
                  pedindo.pedeMotivo ? motivo.trim() : undefined)} />
              <button type="button" className="botao neutro" disabled={ocupado}
                      onClick={() => { setPedindo(null); setMotivo(''); }}>
                Deixar como está
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="cartao">
        <h2>Histórico</h2>
        {hist.length === 0
          ? <Vazio>Nada registrado ainda.</Vazio>
          : (
            <ol className="historico">
              {hist.map((h, i) => (
                <li key={i}>
                  <span className="quando">{dataHora(h.datahora)}</span>
                  <span className="o-que">
                    {h.evento === 'verificacao'
                      ? 'verificado'
                      : h.de ? `${rotulo(h.de)} → ${rotulo(h.para)}`
                             : rotulo(h.para)}
                  </span>
                  <span className="quem">{h.quem ?? 'sistema'}</span>
                  {h.motivo && <span className="motivo">“{h.motivo}”</span>}
                </li>
              ))}
            </ol>
          )}
      </div>

      <p className="rodape-regra">
        Quem executa não verifica nem aprova o próprio ensaio (§15), e o
        banco recusa mesmo que a tela deixasse. Ensaio aprovado não volta:
        a correção é um ensaio novo. <Link to="/fila">Voltar à fila</Link>
      </p>
    </div>
  );
}

function Degrau({ papel, nome, quando }:
                { papel: string; nome: string | null; quando: string | null }) {
  return (
    <li className={nome ? 'feito' : 'pendente'}>
      <span className="papel">{papel}</span>
      {nome
        ? <><span className="nome">{nome}</span>
            <span className="quando">{dataHora(quando)}</span></>
        : <span className="nome vazio">—</span>}
    </li>
  );
}


// ── A emissão ────────────────────────────────────────────────────────

interface Emissao {
  pode: boolean;
  falta: { codigo: string; texto: string }[];
  relatorio: {
    relatorio_id: string; numero: string; revisao: number; status: string;
  } | null;
}

/**
 * O degrau que faltava: emitir.
 *
 * Até a onda 103 todo laudo saía com marca d'água de PRELIMINAR, e ia
 * sair para sempre — emitir nunca tinha sido implementado. O laboratório
 * fazia o ensaio, aprovava, gerava o PDF, e o PDF dizia que era
 * preliminar.
 *
 * Emitir é um ato, e por isso um botão: alguém com `relatorio.emitir`
 * declara que aquele PDF é o documento oficial. O número, a revisão, o
 * arquivo imutável e o hash ficam gravados, e o laudo passa a sair
 * limpo, com o número impresso.
 *
 * Quando o ensaio ainda não pode, a tela diz POR QUÊ — a lista vem do
 * banco, que é quem decide.
 */
function Emitir({ id, em, pode, ocupado, aoEmitir }: {
  id: string;
  em: Emissao | null;
  pode: boolean;
  ocupado: boolean;
  aoEmitir(): void;
}) {
  if (!em) return null;

  if (em.relatorio) {
    const r = em.relatorio;
    return (
      <div className="cartao emitido">
        <h3>
          Laudo {r.numero} · R{String(r.revisao).padStart(2, '0')}{' '}
          <Selo tom="ok">{rotulo(r.status)}</Selo>
        </h3>
        <p className="sub">
          O documento está emitido e guardado, com o hash do arquivo. Ele
          não se sobrescreve: corrigir o que ele diz é gerar uma revisão.
        </p>
        <AcoesDoLaudo ensaioId={id} />
      </div>
    );
  }

  if (!em.pode) {
    /*
       * Classe própria, e não a `.falta-para-subir` da ficha.
       *
       * São duas perguntas diferentes na mesma tela — «o que falta para
       * o ensaio andar» e «o que falta para o laudo sair» —, e a
       * primeira promete que o ensaio sobe sozinho. Compartilhar a
       * classe fazia a segunda ser lida como se fosse a primeira, e o
       * teste 111.61 foi quem mostrou isso.
       */
    return (
      <div className="falta-para-emitir">
        <h3>Falta para o laudo ser emitido</h3>
        <ul>
          {em.falta.map((f) => <li key={f.codigo}>{f.texto}</li>)}
        </ul>
      </div>
    );
  }

  return (
    <div className="cartao confirmacao">
      <h3>Pronto para emitir</h3>
      <p>
        Emitir grava o PDF no acervo, com número, revisão e o hash do
        arquivo — e o laudo deixa de sair como preliminar. O documento
        emitido não se sobrescreve.
      </p>
      {pode ? (
        <SegurarParaConfirmar
          texto={ocupado ? 'Emitindo…' : 'Segure para emitir'}
          tom="normal"
          desabilitado={ocupado}
          aoConfirmar={aoEmitir}
        />
      ) : (
        <p className="sub">
          Emitir exige a permissão <b>relatorio.emitir</b>.
        </p>
      )}
    </div>
  );
}
